import React from "react";
import { Link } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import {
  Mountains,
  Wrench,
  Certificate,
  MedalMilitary,
  ArrowRight,
  Compass,
  Lightning,
  Handshake,
  Trophy,
  GraduationCap,
  InstagramLogo,
  FacebookLogo,
} from "@phosphor-icons/react";

const FOUNDER_IMG = "https://customer-assets.emergentagent.com/job_ecommerce-veloteka/artifacts/modf6e11_IMG_20240818_141633.jpg";

const TIMELINE = [
  { year: "2012", title: "Aktiv Cycling Club & Service", body: "Founded roots at the bicycle club and service Aktiv in Strumica — first years spent on repairs, wrenching, and building community around cycling." },
  { year: "2015 · Germany", title: "Professional Mechanic Abroad", body: "Moved to Germany. Worked for years as a certified bicycle mechanic servicing premium road, MTB, and the first wave of modern e-bikes from Bosch, Yamaha, Brose and Shimano." },
  { year: "2019", title: "VELOTEKA is Born", body: "Rebranded and consolidated more than a decade of experience into VELOTEKA — a professional storefront, workshop, and online presence for cyclists in the Balkans." },
  { year: "Today", title: "One of the Leading Bike Companies in the Balkans", body: "Retail, used bikes, e-bikes, spare parts, a full workshop, bike rental, and guided tours. Same passion, sharper edge." },
];

const CERTIFICATES = [
  {
    title: "Bosch eBike Systems",
    subtitle: "Certified service partner · 2019",
    image: "https://customer-assets-39nsmqrw.emergentagent.net/job_ecommerce-veloteka/artifacts/2yh3vfz0_bosch%20zertifikat%202019.pdf",
    isPdf: true,
  },
  {
    title: "SRAM Technical University",
    subtitle: "RockShox / SRAM brakes & drivetrain · Schweinfurt, 01/2020",
    image: "https://customer-assets-39nsmqrw.emergentagent.net/job_ecommerce-veloteka/artifacts/vd4aswcx_sram%20zertifikat.webp",
    isPdf: false,
  },
];

const EXPERIENCE = [
  { icon: Wrench, title: "14+ Years", body: "Hands-on bicycle mechanics — from steel road bikes to modern carbon e-MTBs." },
  { icon: Lightning, title: "E-Bike Specialist", body: "Bosch, Yamaha, Brose, Shimano STEPS, Bafang — diagnostics, motor and battery service, software updates." },
  { icon: Compass, title: "Germany Trained", body: "Years serving premium brands in a professional German workshop." },
  { icon: Handshake, title: "Customer First", body: "Straight advice, fair prices, no upsell. Every bike gets the same attention as our own." },
];

export default function AboutPage() {
  useSeo({
    title: "Our Story — 14 Years of Wrenches",
    description: "Meet Ivan, founder of VELOTEKA. 14+ years of hands-on bike mechanics, from Aktiv Strumica to certified Bosch e-bike service in Germany.",
    path: "/about",
  });
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="dark-section relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 grid lg:grid-cols-[1.05fr_1fr] gap-12 items-center">
          <div>
            <div className="overline text-[color:var(--vt-orange)] mb-4">/ OUR STORY</div>
            <h1 className="font-display font-black text-white text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.9] mb-6">
              Built on<br />
              <span className="text-[color:var(--vt-orange)]">14 years</span><br />
              of wrenches.
            </h1>
            <p className="text-neutral-300 text-lg max-w-xl">
              VELOTEKA is built on more than a decade of experience. Since 2012, working with bicycles, repairs, and customer service — including many years as a professional bicycle mechanic in Germany, learning premium brands and modern e-bikes. Today VELOTEKA combines that experience into professional sales, service, bike rental, and expert support — with the goal of becoming one of the leading bicycle companies in North Macedonia and the Balkans.
            </p>
          </div>
          <div className="relative">
            <div className="aspect-[3/4] overflow-hidden border-4 border-[color:var(--vt-orange)] shadow-2xl">
              <img src={FOUNDER_IMG} alt="VELOTEKA founder at Tumba peak, 1881m" className="w-full h-full object-cover" data-testid="founder-photo" />
            </div>
            <div className="absolute -bottom-4 -left-4 bg-white px-4 py-3">
              <div className="overline text-neutral-500">Тумба · 1881m</div>
              <div className="font-display font-black text-black">Belasica, MK</div>
            </div>
          </div>
        </div>
      </section>

      {/* MEET THE FOUNDER */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24" data-testid="meet-founder">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-12 items-start">
          <div className="lg:sticky lg:top-32">
            <div className="overline text-[color:var(--vt-orange)] mb-3">/ FOUNDER</div>
            <h2 className="font-display font-black text-5xl tracking-tighter">Meet Ivan.</h2>
            <p className="text-neutral-500 text-sm mt-2">Founder & Head Mechanic</p>
            <div className="flex gap-3 mt-4">
              <a
                href="https://instagram.com/ivan_veloteka"
                target="_blank" rel="noopener noreferrer"
                data-testid="founder-ig"
                className="inline-flex items-center gap-2 border border-neutral-300 hover:border-[color:var(--vt-orange)] hover:text-[color:var(--vt-orange)] px-3 py-2 text-xs font-bold uppercase tracking-wider"
              >
                <InstagramLogo size={14} weight="bold" /> @ivan_veloteka
              </a>
              <a
                href="https://www.facebook.com/andonov.ivan"
                target="_blank" rel="noopener noreferrer"
                data-testid="founder-fb"
                className="inline-flex items-center gap-2 border border-neutral-300 hover:border-[color:var(--vt-orange)] hover:text-[color:var(--vt-orange)] px-3 py-2 text-xs font-bold uppercase tracking-wider"
              >
                <FacebookLogo size={14} weight="bold" /> Andonov Ivan
              </a>
            </div>
          </div>
          <div className="space-y-6 text-lg text-neutral-800 leading-relaxed">
            <p>
              Hi, I'm <strong>Ivan</strong>. With more than <strong>14 years of professional experience</strong> in bicycle mechanics, e-bike technology, and customer service, I created VELOTEKA to give cyclists expert advice, quality products, and professional service.
            </p>
            <p>
              From the bicycle club and service <strong>Aktiv in Strumica</strong> to years of experience in <strong>Germany</strong>, every bike is treated with the same passion and attention to detail. The workshop moved from a single stand to a fully equipped service center — still run by the same hands.
            </p>
            <p className="text-neutral-500 italic border-l-2 border-[color:var(--vt-orange)] pl-6">
              "A bike is more than a machine — it's freedom, health, and the shortest way back to yourself. My job is to keep it running like the day you fell in love with it."
              <br />
              <span className="not-italic text-sm text-neutral-600 mt-2 inline-block">— Ivan Andonov, Founder</span>
            </p>
          </div>
        </div>
      </section>

      {/* EXPERIENCE GRID */}
      <section className="bg-neutral-50 py-24" data-testid="experience-grid">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="mb-12">
            <div className="overline text-[color:var(--vt-orange)] mb-3">/ EXPERIENCE</div>
            <h2 className="font-display font-black text-5xl tracking-tighter">What we bring<br />to every ride.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {EXPERIENCE.map((e, i) => (
              <div key={i} className="bg-white border border-neutral-200 p-6 hover:border-black transition-colors">
                <e.icon size={32} weight="duotone" className="text-[color:var(--vt-orange)] mb-4" />
                <div className="font-display font-black text-2xl mb-2 tracking-tighter">{e.title}</div>
                <p className="text-sm text-neutral-600 leading-relaxed">{e.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24" data-testid="timeline">
        <div className="mb-12">
          <div className="overline text-[color:var(--vt-orange)] mb-3">/ TIMELINE</div>
          <h2 className="font-display font-black text-5xl tracking-tighter">2012 → today.</h2>
        </div>
        <div className="relative border-l-2 border-[color:var(--vt-orange)]/30 pl-10 space-y-14">
          {TIMELINE.map((t, i) => (
            <div key={i} className="relative">
              <div className="absolute -left-[46px] w-5 h-5 bg-[color:var(--vt-orange)] border-4 border-white rounded-full" />
              <div className="overline text-[color:var(--vt-orange)] mb-1">{t.year}</div>
              <h3 className="font-display font-black text-3xl tracking-tighter mb-2">{t.title}</h3>
              <p className="text-neutral-700 max-w-2xl leading-relaxed">{t.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CERTIFICATES */}
      <section className="dark-section py-24" data-testid="certificates">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <div className="overline text-[color:var(--vt-orange)] mb-3">/ CERTIFICATES & ACHIEVEMENTS</div>
              <h2 className="font-display font-black text-5xl tracking-tighter">Trained.<br />Certified. Trusted.</h2>
              <p className="text-neutral-400 mt-3 max-w-xl">Continuous training on the systems and platforms that power modern cycling. Click any certificate to view the original.</p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {CERTIFICATES.map((c, i) => (
              <a
                key={i}
                href={c.image}
                target="_blank" rel="noopener noreferrer"
                className="group border border-white/10 hover:border-[color:var(--vt-orange)] transition-colors p-4 bg-white/[0.02] no-underline-link"
                data-testid={`cert-${i}`}
              >
                <div className="aspect-[4/3] border border-white/10 bg-white overflow-hidden mb-4 flex items-center justify-center">
                  {c.isPdf ? (
                    <div className="text-center p-6">
                      <Certificate size={48} weight="duotone" className="text-[color:var(--vt-orange)] mx-auto mb-3" />
                      <div className="text-neutral-800 text-sm font-bold">Bosch Zertifikat 2019.pdf</div>
                      <div className="text-neutral-500 text-xs mt-1">Click to open PDF ↗</div>
                    </div>
                  ) : (
                    <img src={c.image} alt={c.title} className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105" />
                  )}
                </div>
                <div className="font-display font-bold text-lg text-white mb-1">{c.title}</div>
                <div className="text-xs text-neutral-400">{c.subtitle}</div>
              </a>
            ))}
            <div className="border border-white/10 border-dashed p-6 bg-white/[0.02] flex flex-col items-center justify-center text-center">
              <GraduationCap size={40} weight="duotone" className="text-[color:var(--vt-orange)]/70 mb-3" />
              <div className="font-display font-bold text-lg text-white mb-1">More coming</div>
              <div className="text-xs text-neutral-400">Additional certificates will be added here as they are digitised.</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 text-center">
        <h2 className="font-display font-black text-5xl md:text-6xl tracking-tighter mb-4">Ride with us.</h2>
        <p className="text-neutral-600 max-w-xl mx-auto mb-8">Every bike in our workshop, every product in our store, and every rental you book carries 14+ years of hands-on care.</p>
        <div className="flex flex-wrap gap-3 justify-center">
          <Link to="/shop/new-bikes" className="vt-btn-orange">Shop New Bikes <ArrowRight size={16} weight="bold" /></Link>
          <Link to="/service" className="vt-btn-outline">Book Service</Link>
          <Link to="/contact" className="vt-btn-outline">Contact the Workshop</Link>
        </div>
      </section>
    </div>
  );
}
